<template>
  <div class="edit-form-list-item">
    <div class="history-item">
      <div class="history-item-button">
        <div class="button">
          <el-button
            :disabled="!(index > 0 && length > 1)"
            icon="el-icon-arrow-up"
            circle
            @click.stop="handleCommand('moveUp')"
          ></el-button>
        </div>
        <div class="button">
          <el-button
            :disabled="!(index + 1 != length && length > 1)"
            icon="el-icon-arrow-down"
            circle
            @click.stop="handleCommand('moveDown')"
          ></el-button>
        </div>
      </div>
      <div class="history-item-content">
        <el-form
          class="history-form"
          ref="historyItemForm"
          :model="editableItem"
          :rules="rules"
          @submit.native.prevent
        >
          <div class="history-form-date">
            <el-form-item prop="year">
              <el-input
                class="input-xs"
                placeholder="2000"
                title="年"
                maxlength="4"
                v-model="editableItem.year"
                :min="computedMinYear"
                :max="computedMaxYear"
                ref="year"
              >
              </el-input>
              <span>年</span>
            </el-form-item>
            <el-form-item prop="month">
              <el-input
                class="input-xxs input-2-digit"
                placeholder="12"
                title="月"
                v-model="editableItem.month"
                type="number"
                min="1"
                max="12"
                ref="month"
              >
              </el-input>
              <span>月</span>
            </el-form-item>
          </div>

          <el-form-item prop="description">
            <LicenseNameInput
              v-if="suggestLicense"
              class="input-lg"
              :placeholder="descriptionPlaceholder"
              v-model="editableItem.description"
              ref="desc"
            />
            <el-input
              v-else
              class="input-lg"
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 4 }"
              :placeholder="descriptionPlaceholder"
              v-model="editableItem.description"
              ref="desc"
            ></el-input>
          </el-form-item>

          <div class="errors" v-if="errors">
            <ul>
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>
          <div class="enter text-right">
            <el-button
              icon="el-icon-delete"
              @click.stop="handleCommand('delete')"
              >削除</el-button
            >
            <el-button
              icon="el-icon-check"
              @click.stop="emitter.emit('deactivate-history-item')"
              >OK</el-button
            >
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  ref
} from 'vue'
import { emitter } from '@/plugins/emitter'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import LicenseNameInput from '@/components/format-edit/LicenseNameInput.vue'

type HistoryItem = {
  year?: number | string
  month?: number | string
  description?: string
  uuid?: string
}

type ElFormLike = {
  validate: () => Promise<unknown>
}

type ElInputLike = {
  resizeTextarea?: () => void
  $el?: { offsetHeight?: number }
}

const props = defineProps<{
  item: HistoryItem
  index: number
  length: number
  descriptionPlaceholder?: string
  descriptionMaxLength?: number
  minYear?: number | string
  maxYear?: number | string
  /** 免許・資格欄のとき、入力中に正式名称の候補を出す */
  suggestLicense?: boolean
}>()

const emit = defineEmits<{
  (e: 'add-history-item', index: number, item?: HistoryItem): void
  (e: 'delete-history-item', index: number): void
  (e: 'move-history-item', params: { index: number; to: string }): void
}>()

const { formLib } = useHostServices()
const historyItemForm = ref<ElFormLike | null>(null)
const desc = ref<ElInputLike | null>(null)
const errors = ref<string[]>([])

const editableItem = computed(() => props.item)
const computedMinYear = computed(() =>
  props.minYear ? Number(props.minYear) : 1900
)
const computedMaxYear = computed(() =>
  props.maxYear ? Number(props.maxYear) : new Date().getFullYear() + 3
)

function lengthRule(getMax: () => number, message: string) {
  return [
    {
      validator: (
        _rule: unknown,
        value: string | undefined,
        callback: (err?: Error) => void
      ) => {
        if (formLib.charCount(value) > getMax()) {
          callback(new Error('Please input digits'))
        } else callback()
      },
      message,
      trigger: ['change', 'blur']
    }
  ]
}

const rules = {
  year: lengthRule(() => 6, '年が長すぎます'),
  month: lengthRule(() => 4, '月が長すぎます'),
  description: lengthRule(
    () => props.descriptionMaxLength || 0,
    '一行におさまる文字数をオーバーしています。文章を短くするか、行を ＋ で追加して入力してください。'
  )
}

function handleCommand(command: string) {
  if (command === 'addAbove') emit('add-history-item', props.index)
  else if (command === 'addBelow') emit('add-history-item', props.index + 1)
  else if (command === 'delete') emit('delete-history-item', props.index)
  else if (command === 'duplicate') {
    emit('add-history-item', props.index, props.item)
  } else if (command === 'moveUp') {
    emit('move-history-item', { index: props.index, to: 'up' })
  } else if (command === 'moveDown') {
    emit('move-history-item', { index: props.index, to: 'down' })
  }
}

function resizeDesc() {
  nextTick(() => {
    const target = desc.value
    // 表示されているか確認する
    if (target && typeof target.resizeTextarea === 'function') {
      if (target.$el && target.$el.offsetHeight) target.resizeTextarea()
    }
  })
}

onBeforeMount(() => {
  nextTick(() => {
    // 初回読み込み時にバリデーションを動かす
    historyItemForm.value?.validate().catch((e) => console.error(e))
  })
})

// 表示されていない要素だとリサイズが走らないので、カード変更のタイミングで実行する
onMounted(() => {
  emitter.on('card-changed', resizeDesc)
})

onBeforeUnmount(() => {
  emitter.off('card-changed', resizeDesc)
})
</script>
